import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { EditorPlaceholder } from '@digilize/shared/definitions/src';

import { EMOJI_ICON_PATH } from '../text-editor/emoji-picker-lazy/emoji-icon';
import { loadEmojiPickerComponent } from '../text-editor/emoji-picker-lazy/emoji-picker-loader';

interface PlaceholderGroup {
  label: string;
  placeholders: EditorPlaceholder[];
}

/**
 * "Insert field" menu - and optionally an emoji picker - for a plain text input. Both insert at the
 * caret and write back through the bound control. The menu is grouped when the placeholders carry a
 * `group`, which keeps a long list of merge fields readable.
 */
@Component({
  selector: 'lib-insert-field',
  templateUrl: './insert-field.component.html',
  styleUrls: ['./insert-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InsertFieldComponent implements OnDestroy {
  @Input() set placeholders(placeholders: EditorPlaceholder[]) {
    this._placeholders = placeholders || [];
    this.groups = this.groupPlaceholders(this._placeholders);
  }
  get placeholders(): EditorPlaceholder[] {
    return this._placeholders;
  }

  @Input() control: UntypedFormControl;
  /** The input that receives the field or emoji. Leave unset for a consumer that inserts itself. */
  @Input() target: HTMLInputElement;
  @Input() emojiPicker = false;
  @Input() label = 'ck_editor.insert_field';

  /** The text to insert, for a target this component cannot write to itself (a rich text editor). */
  @Output() inserted = new EventEmitter<string>();

  @ViewChild('emojiButton') emojiButton: ElementRef<HTMLElement>;

  readonly emojiIconPath = EMOJI_ICON_PATH;
  groups: PlaceholderGroup[] = [];
  emojiLoading = false;

  private _placeholders: EditorPlaceholder[] = [];
  private overlayRef?: OverlayRef;
  private caretStart: number | null = null;
  private caretEnd: number | null = null;

  constructor(
    private overlay: Overlay,
    private vcr: ViewContainerRef
  ) {}

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  /**
   * Runs on mousedown, before the input loses focus, so the text lands where the caret was. Without a
   * remembered caret the field is appended instead of jumping to the start of an untouched input.
   */
  rememberCaret(): void {
    const focused = !!this.target && this.target.ownerDocument.activeElement === this.target;

    this.caretStart = focused ? this.target.selectionStart : null;
    this.caretEnd = focused ? this.target.selectionEnd : null;
  }

  /** Braces are normalized the same way the rich editor does, so both insert `{tag}`. */
  insertPlaceholder(placeholder: EditorPlaceholder): void {
    this.insert(`{${(placeholder?.placeholder || '').replace(/[{}]/g, '')}}`);
  }

  async toggleEmojiPicker(): Promise<void> {
    if (this.overlayRef) {
      this.closeEmojiPicker();
      return;
    }

    if (this.emojiLoading || !this.emojiButton) {
      return;
    }

    this.emojiLoading = true;

    try {
      /* Shared module-scoped loader, so the chunk is fetched at most once per session. */
      const { EmojiPickerLazyComponent } = await loadEmojiPickerComponent();

      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.emojiButton)
        .withPositions([
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
        ]);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
      });

      this.overlayRef.backdropClick().subscribe(() => this.closeEmojiPicker());
      this.overlayRef.keydownEvents().subscribe((event) => {
        if (event.key === 'Escape') {
          this.closeEmojiPicker();
        }
      });

      const componentRef = this.overlayRef.attach(new ComponentPortal(EmojiPickerLazyComponent, this.vcr));
      componentRef.instance.emojiSelect.subscribe((native: string) => this.insert(native));
    } finally {
      this.emojiLoading = false;
    }
  }

  private closeEmojiPicker(): void {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    this.target?.focus();
  }

  private insert(text: string): void {
    if (!text) {
      return;
    }

    this.inserted.emit(text);

    if (!this.target) {
      return;
    }

    const value = this.target.value || '';
    const start = this.caretStart ?? value.length;
    const end = this.caretEnd ?? start;
    const caret = start + text.length;

    this.control?.setValue(`${value.slice(0, start)}${text}${value.slice(end)}`);
    this.control?.markAsDirty();

    // The control write moves the caret to the end, so put it back behind the inserted text - picking
    // several emoji in a row then keeps them in order.
    this.target.focus();
    this.target.setSelectionRange(caret, caret);
    this.caretStart = caret;
    this.caretEnd = caret;
  }

  /** Keeps the endpoint's group order; ungrouped placeholders collect under one unlabelled group. */
  private groupPlaceholders(placeholders: EditorPlaceholder[]): PlaceholderGroup[] {
    const groups: PlaceholderGroup[] = [];

    placeholders.forEach((placeholder) => {
      const label = placeholder.group || '';
      let group = groups.find((candidate) => candidate.label === label);

      if (!group) {
        group = { label, placeholders: [] };
        groups.push(group);
      }

      group.placeholders.push(placeholder);
    });

    return groups;
  }
}
